import { Button, Checkbox, Col, Form, Radio, Row, Space } from 'antd';
import React, { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BuddhiMedFileUpload from '../../../../shared/components/fileUpload/buddhimedFileUpload/BuddhiMedFileUpload';
import { CANCELLATION_STATUS_ID, FALL_BACK_CANCEL_MODAL_NAME, GUTTOR_SPACE, VALIDATE_MESSAGES } from '../../../../shared/constants/commonConstants';
import { RootState } from '../../../../store';
import { setReasonForCancellation } from '../../../../store/actions/claimDetailsAction';
import { PayerLookUpState } from '../../../../store/models/lookUpModel';
import { NewAdmissionState } from '../../../../store/models/newAdmissionModel';
import { PayerInitialState } from '../../../../store/models/payerListModel';
import ApplicationDetail from '../../../../shared/services/applicationDetail.service';
import NonHISBuddhimedFileUpload from '../../../../shared/components/fileUpload/buddhimedFileUpload/NonHISBuddhimedFileUpload';
import { HospitalState } from '../../../../store/models/hospitalModel';
import HISBuddhimedFileUpload from '../../../../shared/components/fileUpload/buddhimedFileUpload/HISBuddhimedFileUpload';
import { ctaActionNames, HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW, NEW_UPLOAD_STATUS, NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW } from '../../../../shared/constants/crmConstants';
import { documentUploadFlow, getLettersFromPatientDetails, getCancelModalTitle } from '../service/crmUtils';
import { isEmpty } from 'lodash';

export const ReasonForCancellationModal = (props: any): ReactElement => {
  const { handlers: { form }, isLoading, handleSubmit, buttonText } = props;
  const { claimDetails: { reasonForCancellation } } = useSelector((state: RootState): any => state.admissionDetails);
  const payerLookUp = useSelector((state: RootState): PayerLookUpState => state?.payerLookUp);
  const { payer: selectedPayer } = useSelector((state: RootState): PayerInitialState => state?.payerList);
  const { uniqueIdentifier } = selectedPayer || {};
  const sortedReasonForCancellation = payerLookUp?.[uniqueIdentifier]?.reasonForCancellation?.
    sort((a: any, b: any): number => a?.masterLookId - b?.masterLookId);
  const dispatch = useDispatch();

  const onReasonChange = (e: any): void => {
    dispatch(setReasonForCancellation(e?.target?.value));
  };
  return (
    <React.Fragment>
      <Form form={form} defaultValue={reasonForCancellation} validateMessages={VALIDATE_MESSAGES}>
        <p>
          <strong>Select a reason for claim cancellation</strong>
        </p>
        <Radio.Group defaultValue={reasonForCancellation} onChange={onReasonChange}>
          <Space direction="vertical">
            {
              sortedReasonForCancellation?.map((item: any, index: number): any => (
                <Radio key={index} value={item?.payerMasterValue}>{item?.payerMasterValue}</Radio>
              ))
            }
          </Space>
        </Radio.Group>
        <Row gutter={GUTTOR_SPACE}>
          <Col md={14} className="text-right">
            <Button loading={isLoading}
              disabled={!Boolean(reasonForCancellation)} type={'primary'}
              onClick={handleSubmit}>{buttonText}</Button>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};

export const FinalClaimCancelModal = (props: any): ReactElement => {
  const { handlers: { form }, isChecked, isLoading, handleSubmit, handleCheckBox, setErrorMsg, errorMsg } = props;
  const { claimDetails } = useSelector((state: RootState): any => state.admissionDetails);
  const { uploadedFiles } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const { selectedPatientDetail, hospitalSearchPatientDetails }: any = useSelector((state: RootState): HospitalState => state.hospital);
  const { payer: selectedPayer } = useSelector((state: RootState): PayerInitialState => state?.payerList);
  const { actionProperties } = selectedPayer || {};
  const appDetail = ApplicationDetail.instance;
  const isPreAuthNewDesignEnabled = appDetail?.getIsPreAuthNewDesignEnabled();
  const attachments = getLettersFromPatientDetails(selectedPatientDetail, hospitalSearchPatientDetails);
  const newUploadedFiles = isPreAuthNewDesignEnabled
    ? uploadedFiles?.some((obj: any): boolean => !obj?.existingRecord)
    : uploadedFiles[uploadedFiles?.length - 1]?.claimStatus === NEW_UPLOAD_STATUS;
  const documentFlow = documentUploadFlow(isPreAuthNewDesignEnabled, attachments, claimDetails);
  const cancelActionProperty = JSON.parse(actionProperties || '{}')?.[CANCELLATION_STATUS_ID];
  const isActionPropertiesNotPresent = isEmpty(actionProperties);
  return (
    <Form form={form} validateMessages={VALIDATE_MESSAGES}>
      <>
        {/* His Document Flow */}
        {(documentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW) ? <HISBuddhimedFileUpload form={form} showDragger={true}
          cancelFlow={claimDetails?.showRemarksField}
          remarkRemoved={!claimDetails?.showRemarksField}
          callBuddhimed={false} setErrMsgFromDeleteClaim={setErrorMsg} isCancel={true}
          formTitle={getCancelModalTitle(cancelActionProperty, FALL_BACK_CANCEL_MODAL_NAME)} /> :
          // Non His Document flow
          documentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW ? <NonHISBuddhimedFileUpload form={form}
            showDragger={true} cancelFlow={claimDetails?.showRemarksField}
            remarkRemoved={!claimDetails?.showRemarksField}
            callBuddhimed={false} setErrMsgFromDeleteClaim={setErrorMsg} isCancel={true}
            formTitle={getCancelModalTitle(cancelActionProperty, FALL_BACK_CANCEL_MODAL_NAME)} /> :
            // Default document flow
            <BuddhiMedFileUpload form={form} showDragger={true} cancelFlow={claimDetails?.showRemarksField}
              remarkRemoved={!claimDetails?.showRemarksField}
              callBuddhimed={false} setErrMsgFromDeleteClaim={setErrorMsg} isCancel={true}
              formTitle={getCancelModalTitle(cancelActionProperty, FALL_BACK_CANCEL_MODAL_NAME)} />}
        <div data-testid="delete-claim-modal">
          <p>
            <strong>{claimDetails?.patientName || 'Member'} has not utilized the services and hence we are
              &nbsp;{isActionPropertiesNotPresent ? ctaActionNames.CANCELLING : ctaActionNames.WITHDRAWING}
              &nbsp;the claim from further processing.
            </strong>
          </p>
          <Checkbox onChange={handleCheckBox} checked={isChecked && newUploadedFiles}
            disabled={!newUploadedFiles} data-testid="agreement" />
          <span> I agree that {isActionPropertiesNotPresent ?
            ctaActionNames.CANCELLED : ctaActionNames.WITHDRAWN} claims will be automatically
            &nbsp;{isActionPropertiesNotPresent ? ctaActionNames.CANCELLED : ctaActionNames.WITHDRAWN} on IHX and in Payers system and
            I will not be able to process further.</span>
          <Row gutter={GUTTOR_SPACE}>
            <Col md={16}>
              <div data-testid="error-message" className="error-text fsize_sm">{errorMsg}</div>
            </Col>
            <Col md={8} className="text-right">
              <Button data-testid="cancel-submit" loading={isLoading} className={(!newUploadedFiles || !isChecked) ? 'next-btn' : ''}
                disabled={!newUploadedFiles || !isChecked} type={(!newUploadedFiles || !isChecked) ? undefined : 'primary'}
                onClick={handleSubmit}>Submit</Button>
            </Col>
          </Row>
        </div>
      </>
    </Form>
  );
};
